/**
 * DeviceInspector.tsx — v2.4.0
 *
 * "Show me what this phone is actually doing and what it has actually sent."
 *
 *   STATUS : online/offline, heartbeat, battery, network, GPS state, motion gate,
 *            speed band, tracking active, sync backlog. Auto-refreshes.
 *   DATA   : the points, the clock events, the stops, the fuel, the tickets and
 *            photos, the rotating radio/opportunistic event log, the filter
 *            rejections by reason, and the stillWhileMoving counter.
 *   APPS   : what ELSE is on the phone — and specifically which of those apps is
 *            a battery optimizer / task killer / VPN / mock-location app, i.e.
 *            the thing making the tech go dark. Permission state is shown
 *            honestly: a phone that could not enumerate packages SAYS SO.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Grid, IconButton, MenuItem, Paper, Select, Table, TableBody,
  TableCell, TableHead, TableRow, Tab, Tabs, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon, BatteryFull as BatteryIcon, Wifi as WifiIcon,
  GpsFixed as GpsIcon, CloudSync as SyncIcon, Warning as WarnIcon,
} from '@mui/icons-material';
import { devicesApi } from '../../services/api';

const fmt = (s?: string | null) => {
  if (!s) return '—';
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
};
const num = (v: any, dp = 1) =>
  v === null || v === undefined || v === '' ? '—' : Number(v).toFixed(dp);

/** One headline number. The whole point of the Status tab is that Lyndon can
 *  read the phone's condition in three seconds without opening anything. */
function Stat({ label, value, sub, icon, tone }: {
  label: string; value: React.ReactNode; sub?: string;
  icon?: React.ReactNode; tone?: 'ok' | 'warn' | 'bad';
}) {
  const color = tone === 'bad' ? '#dc2626' : tone === 'warn' ? '#d97706' : 'inherit';
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
        {icon}
        <Typography variant="caption" sx={{ fontWeight: 700 }}>{label}</Typography>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 800, color, lineHeight: 1.3 }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

function Section({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        {title}{count !== undefined && <Chip size="small" label={count} sx={{ ml: 1 }} />}
      </Typography>
      {children}
    </Box>
  );
}

function Empty({ what }: { what: string }) {
  return <Typography variant="body2" color="text.secondary">No {what} received from this device yet.</Typography>;
}

const RISK_COLOR: Record<string, 'error' | 'warning' | 'info'> = {
  mock_location: 'error',
  battery_killer: 'warning',
  task_killer: 'warning',
  vpn: 'info',
};

export default function DeviceInspector({
  deviceId, displayName, onClose,
}: {
  deviceId: string | null; displayName?: string; onClose: () => void;
}) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = useState(0);
  const [hours, setHours] = useState(24);
  const [tel, setTel] = useState<any>(null);
  const [apps, setApps] = useState<any>(null);
  const [ada, setAda] = useState<any>(null);   // v3.0.25 ADA screen capture
  const [status, setStatus] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!deviceId) return;
    setBusy(true); setErr(null);
    try {
      const [t, a, s, ad] = await Promise.all([
        devicesApi.telemetry(deviceId, hours),
        devicesApi.apps(deviceId),
        devicesApi.status(),
        devicesApi.adaCapture(deviceId).catch(() => ({ data: null })),
      ]);
      setTel(t.data);
      setApps(a.data);
      setAda(ad?.data || null);
      setStatus((s.data.devices || []).find((d: any) => d.device_id === deviceId) || null);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not load device data');
    } finally {
      setBusy(false);
    }
  }, [deviceId, hours]);

  useEffect(() => { if (deviceId) { setTab(0); void load(); } }, [deviceId, load]);

  // live-ish: the Status tab keeps itself current while it is open
  useEffect(() => {
    if (!deviceId || tab !== 0) return;
    const t = setInterval(() => { void load(); }, 20000);
    return () => clearInterval(t);
  }, [deviceId, tab, load]);

  const s = status || {};
  const sum = tel?.summary || {};
  const loc = tel?.location || {};

  return (
    <Dialog open={!!deviceId} onClose={onClose} maxWidth="lg" fullWidth fullScreen={phone}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box>
          {displayName || 'Device'}
          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {deviceId}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            data-testid="insp-online"
            label={s.online ? 'ONLINE' : 'OFFLINE'}
            color={s.online ? 'success' : 'default'}
          />
          <Select size="small" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            <MenuItem value={1}>Last 1 h</MenuItem>
            <MenuItem value={24}>Last 24 h</MenuItem>
            <MenuItem value={168}>Last 7 d</MenuItem>
            <MenuItem value={720}>Last 30 d</MenuItem>
          </Select>
          <IconButton onClick={load}>{busy ? <CircularProgress size={20} /> : <RefreshIcon />}</IconButton>
        </Box>
      </DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
        sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab label="Status" data-testid="tab-status" />
        <Tab label="Collected data" data-testid="tab-data" />
        <Tab
          data-testid="tab-apps"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              Apps on phone
              {apps?.problem_count > 0 && (
                <Chip size="small" color="warning" label={apps.problem_count} sx={{ height: 18 }} />
              )}
            </Box>
          }
        />
      </Tabs>

      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        {!tel && busy && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}

        {/* ───────────────────────────── STATUS ───────────────────────────── */}
        {tab === 0 && tel && (
          <>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={4} md={3}>
                <Stat label="Last heartbeat" icon={<SyncIcon fontSize="inherit" />}
                  value={s.seconds_since_seen === null || s.seconds_since_seen === undefined ? '—'
                    : s.seconds_since_seen < 60 ? `${s.seconds_since_seen}s ago`
                    : s.seconds_since_seen < 3600 ? `${Math.floor(s.seconds_since_seen / 60)}m ago`
                    : `${Math.floor(s.seconds_since_seen / 3600)}h ago`}
                  sub={fmt(s.last_seen)}
                  tone={s.online ? 'ok' : 'bad'} />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Stat label="Battery" icon={<BatteryIcon fontSize="inherit" />}
                  value={s.battery_level === null || s.battery_level === undefined ? '—'
                    : `${Math.round(Number(s.battery_level) * (Number(s.battery_level) <= 1 ? 100 : 1))}%`}
                  sub={s.battery_state || 'unknown'}
                  tone={s.battery_level !== null && Number(s.battery_level) <= 0.15 ? 'warn' : undefined} />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Stat label="Network" icon={<WifiIcon fontSize="inherit" />}
                  value={s.network_type || '—'}
                  sub={s.wifi_ssid || s.carrier || (s.network_connected === false ? 'disconnected' : '')}
                  tone={s.network_connected === false ? 'bad' : undefined} />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Stat label="GPS" icon={<GpsIcon fontSize="inherit" />}
                  value={s.gps_fix ? `±${num(s.last_accuracy_m, 0)} m` : 'no fix'}
                  sub={s.last_point_at ? `last fix ${fmt(s.last_point_at)}` : 'never'}
                  tone={s.gps_fix ? undefined : 'warn'} />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Stat label="Speed band / motion gate"
                  value={s.speed_band || '—'} sub={s.motion_state || 'unknown motion state'} />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Stat label="Tracking"
                  value={s.tracking_active ? 'ACTIVE' : 'STOPPED'}
                  sub={s.realtime_mode ? 'realtime mode on' : 'batched'}
                  tone={s.tracking_active ? 'ok' : 'bad'} />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Stat label="Sync backlog"
                  value={s.sync_backlog ?? 0} sub={`${s.upload_failures ?? 0} upload failures`}
                  tone={(s.sync_backlog ?? 0) > 200 ? 'warn' : undefined} />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <Stat label="Points / hour"
                  value={s.points_last_hour ?? 0} sub={`app ${s.app_version || '—'}`} />
              </Grid>
            </Grid>

            {apps?.notices?.length > 0 && (
              <Alert severity="warning" icon={<WarnIcon />} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Permission / interference warnings
                </Typography>
                {apps.notices.map((n: string, i: number) => (
                  <Typography key={i} variant="body2">• {n}</Typography>
                ))}
              </Alert>
            )}

            <Section title="Filter rejections (why fixes were thrown away)"
              count={sum.filter_rejections?.length || 0}>
              {sum.filter_rejections?.length ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {sum.filter_rejections.map((r: any) => (
                    <Chip key={r.reason} size="small" variant="outlined"
                      label={`${r.reason}: ${r.count}`} />
                  ))}
                  <Chip size="small" color={sum.still_while_moving > 0 ? 'warning' : 'default'}
                    data-testid="still-while-moving"
                    label={`stillWhileMoving: ${sum.still_while_moving ?? 0}`} />
                </Box>
              ) : (
                <Empty what="rejection counters (the app has not posted gap telemetry yet)" />
              )}
            </Section>
          </>
        )}

        {/* ─────────────────────────── COLLECTED DATA ─────────────────────── */}
        {tab === 1 && tel && (
          <>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={3}><Stat label="Location points" value={sum.points_in_window ?? 0}
                sub={`${loc.points_per_hour ?? 0}/h · avg ±${num(loc.avg_accuracy_m, 0)} m`} /></Grid>
              <Grid item xs={6} sm={3}><Stat label="Clock events" value={sum.clock_events ?? 0} /></Grid>
              <Grid item xs={6} sm={3}><Stat label="Stops" value={sum.stops ?? 0} /></Grid>
              <Grid item xs={6} sm={3}><Stat label="Fuel records" value={sum.fuel_records ?? 0} /></Grid>
              <Grid item xs={6} sm={3}><Stat label="Tickets touched" value={sum.tickets_touched ?? 0} /></Grid>
              <Grid item xs={6} sm={3}><Stat label="Photos / signatures" value={sum.attachments ?? 0} /></Grid>
              <Grid item xs={6} sm={3}><Stat label="Radio event log" value={sum.event_log_entries ?? 0} /></Grid>
              <Grid item xs={6} sm={3}><Stat label="Sync queue depth" value={sum.sync_queue_depth ?? 0} /></Grid>
            </Grid>

            <Section title={`Recent location points (last ${tel.recent_points?.length || 0})`}>
              {tel.recent_points?.length ? (
                <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>When</TableCell><TableCell>Lat, Lng</TableCell>
                        <TableCell>Acc (m)</TableCell><TableCell>Speed</TableCell>
                        <TableCell>Band</TableCell><TableCell>Source</TableCell><TableCell>On clock</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tel.recent_points.map((p: any, i: number) => (
                        <TableRow key={i} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(p.timestamp)}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                            {num(p.latitude, 5)}, {num(p.longitude, 5)}
                          </TableCell>
                          <TableCell>{num(p.accuracy, 0)}</TableCell>
                          <TableCell>{num(p.speed, 1)}</TableCell>
                          <TableCell>{p.band ? <Chip size="small" label={p.band} variant="outlined" /> : '—'}</TableCell>
                          <TableCell>{p.source || '—'}</TableCell>
                          <TableCell>{p.is_on_clock ? '✓' : ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : <Empty what="location points" />}
            </Section>

            <Section title="Points by speed band">
              {loc.by_band?.length ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {loc.by_band.map((b: any) => (
                    <Chip key={b.band} label={`${b.band}: ${b.points} pts · avg ±${num(b.avg_accuracy_m, 0)} m`}
                      size="small" variant="outlined" />
                  ))}
                </Box>
              ) : <Empty what="banded points" />}
            </Section>

            <Divider sx={{ my: 2 }} />

            <Section title="Time tracking / clock events" count={tel.clock_events?.length || 0}>
              {tel.time_tracking && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Latest shift: {Math.round((tel.time_tracking.shift_seconds || 0) / 60)} min shift ·{' '}
                  {Math.round((tel.time_tracking.job_seconds || 0) / 60)} job ·{' '}
                  {Math.round((tel.time_tracking.travel_seconds || 0) / 60)} travel ·{' '}
                  {Math.round((tel.time_tracking.lunch_seconds || 0) / 60)} lunch
                </Typography>
              )}
              {tel.clock_events?.length ? (
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      <TableCell>When</TableCell><TableCell>Event</TableCell><TableCell>Method</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {tel.clock_events.map((c: any, i: number) => (
                        <TableRow key={i}><TableCell>{fmt(c.timestamp)}</TableCell>
                          <TableCell><Chip size="small" label={c.event_type} variant="outlined" /></TableCell>
                          <TableCell>{c.entry_method}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : <Empty what="clock events" />}
            </Section>

            <Section title="Stops & classifications" count={tel.stops?.length || 0}>
              {tel.stops?.length ? (
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      <TableCell>Arrived</TableCell><TableCell>Mins</TableCell>
                      <TableCell>Classification</TableCell><TableCell>Ticket</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {tel.stops.map((st: any, i: number) => (
                        <TableRow key={i}><TableCell>{fmt(st.arrived_at)}</TableCell>
                          <TableCell>{st.duration_minutes ?? '—'}</TableCell>
                          <TableCell><Chip size="small" label={st.classification || 'unclassified'} variant="outlined" /></TableCell>
                          <TableCell>{st.matched_ticket_id || '—'}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : <Empty what="stop classifications" />}
            </Section>

            <Section title="Fuel / vehicle records" count={tel.fuel?.length || 0}>
              {tel.fuel?.length ? (
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>Filled</TableCell><TableCell>Volume</TableCell><TableCell>Odometer</TableCell>
                    <TableCell>Cost</TableCell><TableCell>Vehicle</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {tel.fuel.map((f: any) => (
                      <TableRow key={f.id}><TableCell>{fmt(f.filled_at)}</TableCell>
                        <TableCell>{num(f.liters, 2)} L</TableCell>
                        <TableCell>{num(f.odometer_km, 0)} km</TableCell>
                        <TableCell>{f.cost ? `$${num(f.cost, 2)}` : '—'}</TableCell>
                        <TableCell>{f.vehicle_id ?? '—'}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <Empty what="fuel records" />}
            </Section>

            <Section title="Tickets touched" count={tel.tickets?.length || 0}>
              {tel.tickets?.length ? (
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      <TableCell>Ticket</TableCell><TableCell>Type</TableCell>
                      <TableCell>Started</TableCell><TableCell>Mins</TableCell><TableCell>Miles</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {tel.tickets.map((t: any, i: number) => (
                        <TableRow key={i}><TableCell>{t.ticket_number || t.ticket_id}</TableCell>
                          <TableCell>{t.entry_type}</TableCell>
                          <TableCell>{fmt(t.started_at)}</TableCell>
                          <TableCell>{t.duration_minutes ?? '—'}</TableCell>
                          <TableCell>{num(t.distance_miles, 1)}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : <Empty what="ticket time entries" />}
            </Section>

            <Section title="Photos & signatures" count={tel.attachments?.length || 0}>
              {tel.attachments?.length ? (
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell>File</TableCell><TableCell>Ticket</TableCell>
                    <TableCell>Type</TableCell><TableCell>Size</TableCell><TableCell>Uploaded</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {tel.attachments.map((a: any) => (
                      <TableRow key={a.id}><TableCell>{a.original_filename}</TableCell>
                        <TableCell>{a.ticket_id}</TableCell>
                        <TableCell>{a.content_type}</TableCell>
                        <TableCell>{a.file_size ? `${Math.round(a.file_size / 1024)} KB` : '—'}</TableCell>
                        <TableCell>{fmt(a.created_at)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <Empty what="photos or signatures" />}
            </Section>

            <Section title="ADA screen captures" count={ada?.captures?.length || 0}>
              {ada?.state ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip size="small" color={ada.state.effective ? 'success' : 'default'} label={ada.state.effective ? 'Active on this phone' : `Not active (${ada.state.reason})`} />
                  <Chip size="small" variant="outlined" color={ada.state.sso_granted ? 'success' : 'default'} label={`SSO grant ${ada.state.cap}: ${ada.state.sso_granted ? 'YES' : 'no'}`} />
                  <Chip size="small" variant="outlined" label={`This phone: ${ada.state.device_allow ? 'allowed' : 'OFF'}`} />
                  <Chip size="small" variant="outlined" label={`Global: ${ada.state.global_allow ? 'allowed' : 'OFF'}`} />
                </Box>
              ) : <Typography variant="body2" color="text.secondary">State unavailable (tracker API).</Typography>}
              {ada?.captures?.length ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1 }}>
                  {ada.captures.map((c: any) => (
                    <a key={c.id} href={c.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                      <img src={c.url} alt={`capture ${c.captured_at}`} loading="lazy" style={{ width: '100%', borderRadius: 6, border: '1px solid #ddd' }} />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{fmt(c.captured_at)}{c.foreground_app ? ` · ${c.foreground_app}` : ''}</Typography>
                    </a>
                  ))}
                </Box>
              ) : <Empty what="ADA screen captures" />}
            </Section>

            <Section title="Rotating radio / opportunistic-sync event log" count={tel.event_log?.length || 0}>
              {tel.event_log?.length ? (
                <Box sx={{ maxHeight: 240, overflow: 'auto', bgcolor: '#0f172a', borderRadius: 1, p: 1 }}>
                  {tel.event_log.map((e: any, i: number) => (
                    <Typography key={i} sx={{ fontFamily: 'monospace', fontSize: 11, color: '#a7f3d0' }}>
                      {fmt(e.ts)} [{e.type}] {e.trigger_reason} {e.detail || ''}
                    </Typography>
                  ))}
                </Box>
              ) : <Empty what="radio / opportunistic-sync events" />}
            </Section>
          </>
        )}

        {/* ───────────────────────────── APPS ─────────────────────────────── */}
        {tab === 2 && apps && (
          <>
            {apps.notices?.map((n: string, i: number) => (
              <Alert key={i} severity={n.includes('MOCK') ? 'error' : 'warning'} sx={{ mb: 1 }}>{n}</Alert>
            ))}

            {apps.reported && (
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}><Stat label="Apps reported" value={apps.reported_count ?? 0}
                  sub={apps.total_packages ? `of ${apps.total_packages} on device` : undefined} /></Grid>
                <Grid item xs={6} sm={3}><Stat label="Problem apps" value={apps.problem_count}
                  tone={apps.problem_count ? 'bad' : 'ok'} /></Grid>
                <Grid item xs={6} sm={3}><Stat label="Package visibility"
                  value={apps.permissions.query_all_packages === null ? '—'
                    : apps.permissions.query_all_packages ? 'granted' : 'DENIED'}
                  tone={apps.permissions.query_all_packages === false ? 'warn' : 'ok'} /></Grid>
                <Grid item xs={6} sm={3}><Stat label="Usage stats"
                  value={apps.permissions.usage_stats_granted === null ? '—'
                    : apps.permissions.usage_stats_granted ? 'granted' : 'DENIED'}
                  tone={apps.permissions.usage_stats_granted === false ? 'warn' : 'ok'} /></Grid>
              </Grid>
            )}

            {apps.problem_apps?.length > 0 && (
              <Section title="Apps interfering with location / battery" count={apps.problem_count}>
                <Table size="small" data-testid="problem-apps">
                  <TableHead><TableRow>
                    <TableCell>App</TableCell><TableCell>Risk</TableCell>
                    <TableCell>Why it matters</TableCell><TableCell>Running</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {apps.problem_apps.map((a: any) => (
                      <TableRow key={a.package_name} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{a.app_label || a.package_name}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {a.package_name}{a.is_system ? ' • system' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={RISK_COLOR[a.risk] || 'default'} label={a.risk_label || a.risk} />
                        </TableCell>
                        <TableCell><Typography variant="caption">{a.risk_reason}</Typography></TableCell>
                        <TableCell>{a.is_running ? <Chip size="small" color="warning" label="running" /> : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Section>
            )}

            <Section title="All apps on this phone" count={apps.apps?.length || 0}>
              {apps.apps?.length ? (
                <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>
                      <TableCell>App</TableCell><TableCell>Version</TableCell>
                      <TableCell>Foreground time</TableCell><TableCell>Last used</TableCell>
                      <TableCell>State</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {apps.apps.map((a: any) => (
                        <TableRow key={a.package_name} hover
                          sx={{ bgcolor: a.risk ? 'rgba(251,191,36,0.08)' : undefined }}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: a.risk ? 700 : 500 }}>
                              {a.app_label || a.package_name}
                              {a.risk && (
                                <Tooltip title={a.risk_reason || ''}>
                                  <Chip size="small" sx={{ ml: 1, height: 18 }}
                                    color={RISK_COLOR[a.risk] || 'default'} label={a.risk_label} />
                                </Tooltip>
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {a.package_name}
                            </Typography>
                          </TableCell>
                          <TableCell>{a.version_name || '—'}</TableCell>
                          <TableCell>
                            {a.foreground_time_ms
                              ? `${Math.round(a.foreground_time_ms / 60000)} min`
                              : <Typography variant="caption" color="text.disabled">n/a</Typography>}
                          </TableCell>
                          <TableCell>{a.last_used_at ? fmt(a.last_used_at) : '—'}</TableCell>
                          <TableCell>
                            {a.is_foreground ? <Chip size="small" color="primary" label="foreground" />
                              : a.is_running ? <Chip size="small" label="running" variant="outlined" />
                              : !a.enabled ? <Chip size="small" label="disabled" variant="outlined" />
                              : a.is_system ? <Typography variant="caption" color="text.disabled">system</Typography>
                              : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Alert severity="info">
                  No app inventory from this phone yet. The tracker app posts it to{' '}
                  <code>POST /api/device/app-inventory</code> on pair, on upgrade and daily. If the phone
                  has not been granted package visibility it will report an empty list and say so here.
                </Alert>
              )}
            </Section>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
